import { useMemo, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BillingState,
  GetProgramEnrollmentPaymentPlansQuery,
  PaymentPlanStatus,
} from '@sandtech-hub-monorepo/ehub-client';

import { Button, cn } from '@sandtech-hub-monorepo/ui-kit';
import { AlertCircle, CheckCircle, ChevronRight, Info } from 'lucide-react';
import { differenceInDays, differenceInHours } from 'date-fns';
import { ExternalApplicationLinks } from '../../../utils';
import { formatDate } from '../../dateFormat';

type AlertLevel = 'blue' | 'amber' | 'red' | 'success' | null;

// Context passed to message generator functions
type PaymentMessageContext = {
  formattedDate: string;
  nextAmount: number;
  outstandingBalance: number;
  isAllAccess: boolean;
};

type MessageGenerator = (ctx: PaymentMessageContext) => ReactNode;

type LevelMessages = {
  blue: MessageGenerator;
  amber: MessageGenerator;
  red: MessageGenerator;
};

const SEVERITY: AlertLevel[] = [null, 'blue', 'amber', 'red'];

// The payments team's billing-state table (PEDS-13224) mapped onto the
// spec's colours: blue 10 days before, amber <48h, red due/overdue. INACTIVE
// is an all-access plan with nothing paid yet (PEDS-16021 "Payment due").
const BILLING_STATE_LEVEL: Partial<Record<BillingState, AlertLevel>> = {
  [BillingState.DueSoon]: 'blue',
  [BillingState.DueNow]: 'amber',
  [BillingState.Inactive]: 'amber',
  [BillingState.Grace]: 'red',
  [BillingState.AtRisk]: 'red',
  [BillingState.InArrears]: 'red',
};

const overdue: MessageGenerator = ({ nextAmount }) => (
  <>
    <b>Payment overdue:</b> Payment of <b>${nextAmount.toFixed(2)}</b> is now
    overdue, please make a payment urgently to avoid disruption to your
    learning.
  </>
);

// PEDS-16021: enrolled but not on an active plan, i.e. an INACTIVE plan or
// one the event processor hasn't created yet.
const FIRST_PAYMENT_DUE = (
  <>
    <b>Payment due:</b> Complete your first payment to unlock All Access.
  </>
);

// ALX (central-hub) only; FIT has its own FitProgramEnrollmentPaymentBanner.
// Sources of truth: PEDS-13224 (colours, pathway terms), PEDS-15503 (unpaid,
// overdue and paid-up banners), PEDS-16017 / PEDS-16021 (first-payment copy).
const MESSAGES: {
  success: ReactNode;
  fallback: { red: ReactNode; amber: ReactNode; blue: ReactNode };
  beforeFirst: LevelMessages;
  transitionNoOutstanding: LevelMessages;
  transitionWithOutstanding: LevelMessages;
} = {
  success: (
    <>
      <b>Payment up to date.</b> Your fees are up to date, ensuring
      uninterrupted access to your course. Keep it up!
    </>
  ),
  fallback: {
    red: (
      <>
        <b>Payment required.</b> Please make a payment.
      </>
    ),
    amber: (
      <>
        <b>Payment due soon.</b> Please make a payment.
      </>
    ),
    blue: (
      <>
        <b>Payment coming up.</b> Please make a payment.
      </>
    ),
  },
  beforeFirst: {
    blue: ({ formattedDate, isAllAccess }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDate}</b> to ensure uninterrupted learning
        {!isAllAccess && ' and begin the term'}.
      </>
    ),
    amber: () => FIRST_PAYMENT_DUE,
    red: overdue,
  },
  transitionNoOutstanding: {
    blue: ({ formattedDate }) => (
      <>
        <b>Payment coming up:</b> Make your first payment by{' '}
        <b>{formattedDate}</b> to advance to the next term.
      </>
    ),
    amber: ({ formattedDate }) => (
      <>
        <b>Payment due soon:</b> Make your first payment by{' '}
        <b>{formattedDate}</b> to advance to the next term.
      </>
    ),
    red: overdue,
  },
  transitionWithOutstanding: {
    blue: ({ formattedDate, outstandingBalance }) => (
      <>
        <b>Payment coming up:</b> Make your first payment of <b>$5</b> and clear
        outstanding balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b> to advance to the next term.
      </>
    ),
    amber: ({ formattedDate, outstandingBalance }) => (
      <>
        <b>Payment due soon:</b> Make your first payment of <b>$5</b> and clear
        outstanding balance <b>${outstandingBalance.toFixed(2)}</b> by{' '}
        <b>{formattedDate}</b> to advance to the next term.
      </>
    ),
    red: overdue,
  },
};

interface Props {
  paymentPlan?: GetProgramEnrollmentPaymentPlansQuery['viewer']['paymentPlans'][number];
  paymentFAQsLink: string;
  showPaymentPlanName?: boolean;
  showFAQLink?: boolean;
}

export const AlxProgramEnrollmentPaymentBanner = ({
  paymentPlan,
  paymentFAQsLink,
  showPaymentPlanName = true,
  showFAQLink = true,
}: Props) => {
  const navigate = useNavigate();

  const { alertLevel, message, showBanner } = useMemo(() => {
    // Rendered without a plan only for an ongoing all-access enrollment whose
    // plan the event processor hasn't created yet.
    if (!paymentPlan) {
      return {
        alertLevel: 'amber' as AlertLevel,
        message: FIRST_PAYMENT_DUE,
        showBanner: true,
      };
    }
    if (
      !paymentPlan.status ||
      paymentPlan.status === PaymentPlanStatus.Cancelled
    ) {
      return { showBanner: false };
    }

    const {
      billingState,
      totalPaidAmount = 0,
      nextInstalmentDate,
      amountPerInstalment,
    } = paymentPlan;
    const isAllAccess = paymentPlan.code === 'all-access';

    // If payment is completed or up to date, show success banner
    if (
      [
        BillingState.UpToDate,
        BillingState.InAdvance,
        BillingState.Completed,
      ].includes(billingState)
    ) {
      return {
        alertLevel: 'success' as AlertLevel,
        message: MESSAGES.success,
        showBanner: true,
      };
    }

    // The payment service no longer keeps invoices for these plans, so the
    // due date is the plan's next instalment date (PEDS-16073).
    const dueDate = new Date(nextInstalmentDate);
    const now = new Date();
    const daysUntilDue = differenceInDays(dueDate, now);
    const hoursUntilDue = differenceInHours(dueDate, now);

    // Determine alert level based on time until due
    let level: AlertLevel = null;
    if (daysUntilDue < 0) {
      level = 'red'; // Overdue
    } else if (hoursUntilDue < 48) {
      level = 'amber'; // Due soon (<48 hours)
    } else if (daysUntilDue <= 10) {
      level = 'blue'; // Coming up (10 days before)
    } else if (isAllAccess) {
      level = 'blue'; // Always show all-access banner
    }

    // Unlike the old invoice due date, nextInstalmentDate is the next
    // *scheduled* instalment, so a learner already behind can have it weeks
    // away. The billing state raises the level so an owing plan always shows.
    const stateLevel = BILLING_STATE_LEVEL[billingState] ?? null;
    if (SEVERITY.indexOf(stateLevel) > SEVERITY.indexOf(level)) {
      level = stateLevel;
    }
    if (!level) {
      return { showBanner: false };
    }

    // Convert cents to dollars
    const totalPaid = totalPaidAmount / 100;
    const nextAmount = amountPerInstalment / 100;

    // Pathway terms (PEDS-13224): $20 per term in $5 increments. Term 1 is
    // due until $5 is paid, term 2 until $25. All-access has no terms.
    const isFirstPayment = totalPaid < 5;
    const isTransition = !isAllAccess && totalPaid >= 5 && totalPaid < 25;
    const outstandingBalance = isTransition ? Math.max(0, 20 - totalPaid) : 0;

    const ctx: PaymentMessageContext = {
      formattedDate: formatDate(dueDate),
      nextAmount,
      outstandingBalance,
      isAllAccess,
    };

    const levelKey = level as 'blue' | 'amber' | 'red';
    const bannerMessage: ReactNode = isFirstPayment
      ? MESSAGES.beforeFirst[levelKey](ctx)
      : isTransition
      ? (outstandingBalance > 0
          ? MESSAGES.transitionWithOutstanding
          : MESSAGES.transitionNoOutstanding)[levelKey](ctx)
      : MESSAGES.fallback[levelKey];

    return {
      alertLevel: level,
      message: bannerMessage,
      showBanner: true,
    };
  }, [paymentPlan]);

  if (!showBanner) {
    return null;
  }

  const isError = alertLevel === 'red';
  const isSuccess = alertLevel === 'success';
  const isInfo = alertLevel === 'blue';
  const isWarning = alertLevel === 'amber';

  return (
    <div
      className={cn(
        'px-4 py-3 lg:py-1 flex flex-col sm:flex-row gap-2 lg:gap-4 items-center border w-full rounded-lg',
        isError ? 'border-border-error bg-destructive/10 text-destructive' : '',
        isSuccess ? 'border-success bg-success/10 text-success' : '',
        isInfo ? 'border-info bg-info/10 text-info' : '',
        isWarning ? 'border-warning bg-warning/10 text-warning' : ''
      )}
    >
      {isError && <AlertCircle className="stroke-destructive h-6 w-6" />}
      {isSuccess && <CheckCircle className="stroke-success min-h-6 min-w-6" />}
      {(isInfo || isWarning) && (
        <Info className="stroke-current min-h-6 min-w-6" />
      )}
      {showPaymentPlanName && <b>{paymentPlan?.definition?.name}</b>}
      <div className="text-sm">
        {message}{' '}
        {showFAQLink && !isSuccess && (
          <span>
            Read our{' '}
            <Button
              className={`h-fit p-0 font-semibold underline ${
                isError
                  ? 'text-error'
                  : isWarning
                  ? 'text-warning'
                  : 'text-primary'
              }`}
              variant={'link'}
              onClick={() => navigate(paymentFAQsLink)}
            >
              payments FAQ
            </Button>
            .
          </span>
        )}
      </div>
      {paymentPlan?.billingState !== BillingState.Completed && (
        <Button
          variant={
            isError
              ? 'ghostDestructive'
              : isInfo
              ? 'ghostInfo'
              : isWarning
              ? 'ghostWarning'
              : 'ghostSuccess'
          }
          className="mx-auto sm:ml-auto sm:mr-0 p-0"
          onClick={() =>
            window.location.replace(
              paymentPlan?.viewUrl || ExternalApplicationLinks.payments
            )
          }
        >
          {isError && 'Pay now'}
          {isSuccess && 'Make a Payment'}
          {(isInfo || isWarning) && 'Make payment now'}
          <ChevronRight />
        </Button>
      )}
    </div>
  );
};
